import PropTypes from "prop-types"

const BETA_METRICS = {
  historical: PropTypes.number,
  predicted: PropTypes.number,
  dynamicPredicted: PropTypes.number,
}

const FACTOR_METRICS = PropTypes.shape({
  category: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.number,
})

const EXPOSURE_METRICS = {
  factors: PropTypes.objectOf(FACTOR_METRICS),
}

const FORECAST_METRICS = {
  totalExpectedReturn: PropTypes.number,
  securityExpectedReturn: PropTypes.number,
  weightedExpectedReturn: PropTypes.number,
}

const FACTOR_SENSITIVITY = {
  factors: PropTypes.objectOf(FACTOR_METRICS),
}

const MARGINAL_CONTRIBUTION_METRICS = PropTypes.shape({
  correlation: PropTypes.number,
  marginalContribution: PropTypes.number,
  riskContribution: PropTypes.number,
  volatility: PropTypes.number,
})

const RISK_METRICS = {
  factors: PropTypes.objectOf(FACTOR_METRICS),
  summary: PropTypes.shape({
    factors: PropTypes.number,
    specific: PropTypes.number,
    total: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  }),
}

// The contributors property inside of the metrics only appears when displaying groupedContributors
const contributor = {
  id: PropTypes.string,
  isAggregated: PropTypes.bool,
  isDrilldown: PropTypes.bool,
  action: PropTypes.func,
  activePercentEquity: PropTypes.number,
  baseEconomicExposure: PropTypes.number,
  economicExposure: PropTypes.number,
  benchmarkPercentEquity: PropTypes.number,
  beta: PropTypes.shape({
    ...BETA_METRICS,
    contributors: PropTypes.objectOf(PropTypes.shape(BETA_METRICS)),
  }),
  exposure: PropTypes.shape({
    ...EXPOSURE_METRICS,
    contributors: PropTypes.objectOf(PropTypes.shape(EXPOSURE_METRICS)),
  }),
  forecast: PropTypes.shape({
    ...FORECAST_METRICS,
    contributors: PropTypes.objectOf(PropTypes.shape(FORECAST_METRICS)),
  }),
  historicalFactorSensitivity: PropTypes.shape({
    ...FACTOR_SENSITIVITY,
    contributors: PropTypes.objectOf(PropTypes.shape(FACTOR_SENSITIVITY)),
  }),
  predictedFactorSensitivity: PropTypes.shape({
    ...FACTOR_SENSITIVITY,
    contributors: PropTypes.objectOf(PropTypes.shape(FACTOR_SENSITIVITY)),
  }),
  marginal: PropTypes.shape({
    factors: MARGINAL_CONTRIBUTION_METRICS,
    specific: MARGINAL_CONTRIBUTION_METRICS,
    total: MARGINAL_CONTRIBUTION_METRICS,
  }),
  risk: PropTypes.shape({
    ...RISK_METRICS,
    contributors: PropTypes.objectOf(PropTypes.shape(RISK_METRICS)),
  }),
  // Only when displaying groupedContributors
  contributors: PropTypes.object,
}

export const CONTRIBUTOR = PropTypes.shape(contributor)

export const CONTRIBUTOR_WITH_ADV = PropTypes.shape({
  ...contributor,
  averageDailyVolume: PropTypes.number,
  isInOriginalQuery: PropTypes.bool,
})

export const CHANGE = PropTypes.shape({
  ticker: PropTypes.string.isRequired,
  mic: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  field: PropTypes.oneOf(["gmv", "percentEquity", "activePercentEquity"]).isRequired,
})

export const COLUMN = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  allowAbsSort: PropTypes.bool,
  canAggregate: PropTypes.bool,
  editable: PropTypes.bool,
  isSticky: PropTypes.bool,
  sorted: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  formatter: PropTypes.func,
  outputFormatter: PropTypes.oneOf(["number", "percent", "currency", "date"]),
  path: PropTypes.string.isRequired,
})

export const COLUMN_GROUP = PropTypes.shape({
  category: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(COLUMN).isRequired,
  getter: PropTypes.func.isRequired,
})

export const FORECAST = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
})

export const TRADED_POSITION = PropTypes.shape({
  assetClass: PropTypes.string,
  bloombergTicker: PropTypes.string,
  classification: PropTypes.string,
  country: PropTypes.string,
  currency: PropTypes.string,
  factorsTotal: PropTypes.number,
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  mic: PropTypes.string.isRequired,
  negative: PropTypes.bool,
  rebalancedFactorsTotal: PropTypes.number,
  rebalancedSpecific: PropTypes.number,
  rebalancedTotal: PropTypes.number,
  securityPercentEquity: PropTypes.number,
  specific: PropTypes.number,
  ticker: PropTypes.string.isRequired,
  total: PropTypes.number,
  tradePercentDelta: PropTypes.number,
  traded: PropTypes.bool,
})

export const TRADES = PropTypes.shape({
  all: PropTypes.arrayOf(TRADED_POSITION),
  buy: PropTypes.arrayOf(TRADED_POSITION),
  cover: PropTypes.arrayOf(TRADED_POSITION),
  sell: PropTypes.arrayOf(TRADED_POSITION),
  short: PropTypes.arrayOf(TRADED_POSITION),
})

export const SIMULATE_ACTION = PropTypes.shape({
  tradeSecurity: PropTypes.shape({
    id: PropTypes.shape({
      ticker: PropTypes.string,
      mic: PropTypes.string,
      bloombergTicker: PropTypes.string,
      figi: PropTypes.string,
      internalId: PropTypes.string,
    }).isRequired,
    operation: PropTypes.shape({
      setEconomicExposure: PropTypes.number,
      setPercentEquity: PropTypes.number,
      setActivePercentEquity: PropTypes.number,
    }).isRequired,
  }),
})
